<template>
  <div class="min-h-screen bg-[#000] relative overflow-hidden">
    <!-- iOS Status Bar Spacer -->
    <div class="h-16"></div>

    <!-- Date Navigation -->
    <div class="px-8 pb-4 flex items-center justify-between">
      <button
        @click="previousDay"
        class="text-white/40 active:opacity-70 transition-opacity"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <p class="text-[17px] text-white/60 lowercase">{{ currentDateLabel }}</p>

      <button
        @click="nextDay"
        class="text-white/40 active:opacity-70 transition-opacity"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Task List -->
    <div class="px-8 pb-32">
      <div v-if="filteredTasks.length === 0" class="pt-4">
        <p class="text-[22px] text-white/40 lowercase">no tasks yet</p>
      </div>

      <div
        v-for="task in filteredTasks"
        :key="task.id"
        class="py-3 active:opacity-70 transition-opacity"
        @click="toggleTaskCompletion(task.id)"
      >
        <p
          class="text-[22px]"
          :class="
            task.isCompleted ? 'text-white/30 line-through' : 'text-white'
          "
        >
          {{ task.title }}
        </p>
      </div>
    </div>

    <!-- Floating Add Button -->
    <button
      @click="openModal"
      class="fixed bottom-8 right-8 w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-transform z-50"
    >
      <svg
        class="w-7 h-7 text-[#2c3439]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        stroke-width="2.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 6v12m6-6H6"
        />
      </svg>
    </button>

    <!-- Modal -->
    <transition name="modal">
      <div v-if="showModal" class="fixed inset-0 z-50" @click.self="closeModal">
        <div
          class="absolute inset-0 bg-black/70 backdrop-blur-sm"
          @click="closeModal"
        ></div>
        <div
          class="absolute bottom-0 left-0 right-0 bg-[#2c3439] rounded-t-[28px] shadow-2xl transform transition-all duration-300"
        >
          <div class="px-8 py-8 pb-12">
            <input
              type="text"
              v-model="newTaskTitle"
              ref="taskInput"
              @keydown.enter="addNewTask"
              placeholder="new task"
              class="w-full px-0 py-3 bg-transparent text-white text-[22px] lowercase focus:outline-none placeholder-white/40 border-b border-white/20"
            />

            <div class="flex gap-3 mt-6">
              <button
                @click="addNewTask"
                class="flex-1 py-3 bg-white text-[#2c3439] text-[17px] font-medium rounded-full active:opacity-70 transition-opacity"
              >
                add
              </button>

              <button
                @click="closeModal"
                class="flex-1 py-3 bg-white/10 text-white text-[17px] font-medium rounded-full active:opacity-70 transition-opacity"
              >
                cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import { nextTick } from "vue";
import Echo from "laravel-echo";
import Pusher from "pusher-js";

// Set up Laravel Echo with Pusher
window.Pusher = Pusher;

window.Echo = new Echo({
  broadcaster: "pusher",
  key: "2cebcefb627c1ac098a5", // Replace with your Pusher key
  cluster: "eu", // Replace with your Pusher cluster
  encrypted: true,
});

export default {
  data() {
    return {
      selectedTab: {
        name: "Today",
        date: new Date().toISOString().split("T")[0],
      },
      showModal: false,
      newTaskTitle: "",
      isRefreshing: false,
      tabs: [
        {
          name: "Yesterday",
          date: new Date(Date.now() - 864e5).toISOString().split("T")[0],
        },
        {
          name: "Today",
          date: new Date().toISOString().split("T")[0],
        },
        {
          name: "Tomorrow",
          date: new Date(Date.now() + 864e5).toISOString().split("T")[0],
        },
      ],
      tasks: {}, // Will store tasks grouped by date
    };
  },
  computed: {
    filteredTasks() {
      return this.tasks[this.selectedTab.date] || [];
    },
    completedCount() {
      return this.filteredTasks.filter((task) => task.isCompleted).length;
    },
    pendingCount() {
      return this.filteredTasks.filter((task) => !task.isCompleted).length;
    },
    currentDateLabel() {
      const today = new Date().toISOString().split("T")[0];
      const yesterday = new Date(Date.now() - 864e5).toISOString().split("T")[0];
      const tomorrow = new Date(Date.now() + 864e5).toISOString().split("T")[0];

      if (this.selectedTab.date === today) return "today";
      if (this.selectedTab.date === yesterday) return "yesterday";
      if (this.selectedTab.date === tomorrow) return "tomorrow";

      // Return formatted date for other days
      const date = new Date(this.selectedTab.date);
      const day = date.getDate();
      const month = date.toLocaleDateString("en-US", { month: "short" });
      return `${day} ${month}`;
    },
  },
  methods: {
    previousDay() {
      const currentDate = new Date(this.selectedTab.date);
      currentDate.setDate(currentDate.getDate() - 1);
      const newDate = currentDate.toISOString().split("T")[0];

      this.selectedTab = {
        name: this.getDateName(newDate),
        date: newDate,
      };
    },
    nextDay() {
      const currentDate = new Date(this.selectedTab.date);
      currentDate.setDate(currentDate.getDate() + 1);
      const newDate = currentDate.toISOString().split("T")[0];

      this.selectedTab = {
        name: this.getDateName(newDate),
        date: newDate,
      };
    },
    getDateName(dateString) {
      const today = new Date().toISOString().split("T")[0];
      const yesterday = new Date(Date.now() - 864e5).toISOString().split("T")[0];
      const tomorrow = new Date(Date.now() + 864e5).toISOString().split("T")[0];

      if (dateString === today) return "Today";
      if (dateString === yesterday) return "Yesterday";
      if (dateString === tomorrow) return "Tomorrow";

      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString("en-US", { month: "short" });
      return `${day} ${month}`;
    },
    async openModal() {
      this.showModal = true;
      nextTick(() => {
        this.$refs.taskInput.focus();
      });
    },
    closeModal() {
      this.showModal = false;
    },
    async addNewTask() {
      if (this.newTaskTitle) {
        const newTask = {
          id: Math.floor(Math.random() * 1000),
          title: this.newTaskTitle,
          start: `${this.selectedTab.date} 10:00`,
          end: `${this.selectedTab.date} 11:00`,
          isCompleted: false,
        };

        const data = {
          [this.selectedTab.date]: [newTask],
        };

        try {
          // Send the new task to the API
          await axios.post("https://todo-api.g-home.site/api/v2/todos", data);
          this.refreshTasks(); // Reload tasks after adding
        } catch (error) {
          console.error("Failed to add new task", error);
        }

        this.newTaskTitle = "";
        this.closeModal();
      }
    },
    async toggleTaskCompletion(taskId) {
      const task = this.filteredTasks.find((task) => task.id === taskId);
      task.isCompleted = !task.isCompleted;

      try {
        await axios.patch(
          `https://todo-api.g-home.site/api/v2/todos/${taskId}`,
          task
        );
        this.refreshTasks(); // Reload tasks after toggling completion
      } catch (error) {
        console.error("Failed to update task", error);
      }
    },
    async refreshTasks() {
      this.isRefreshing = true;
      try {
        // Fetch tasks from the API grouped by date
        const response = await axios.get(
          "https://todo-api.g-home.site/api/v2/todos"
        );
        this.tasks = response.data;
        console.log(this.selectedTab.date);

        await axios.post("https://notify-api.g-home.site/notify", {
          tasks: this.filteredTasks,
        });
      } catch (error) {
        console.error("Failed to load tasks", error);
      } finally {
        setTimeout(() => {
          this.isRefreshing = false;
        }, 500);
      }
    },
    listenForTaskEvents() {
      window.Echo.channel("tasks").listen("TaskCreated", () => {
        this.refreshTasks();
      });
      window.Echo.channel("tasks").listen("TaskUpdated", () => {
        this.refreshTasks();
      });
      window.Echo.channel("tasks").listen("TaskDeleted", () => {
        this.refreshTasks();
      });
    },
  },
  async mounted() {
    await this.refreshTasks(); // Load tasks when the component is mounted
    this.listenForTaskEvents(); // Set up real-time listeners
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=SF+Pro+Display:wght@400;500;600;700&display=swap");

* {
  -webkit-tap-highlight-color: transparent;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active > div:last-child,
.modal-leave-active > div:last-child {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from > div:last-child {
  transform: translateY(100%);
}

.modal-leave-to > div:last-child {
  transform: translateY(100%);
}

@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .pb-safe {
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
